import { Button } from "@datapay-by-datacom/design-system";
import type { MatchingCsvAction } from "../types/matching";
import styles from "./MatchingBulkActionBar.module.css";

export type MatchingBulkScope = "selection" | "all";

const VERB_BUTTONS: ReadonlyArray<{
  action: MatchingCsvAction;
  label: string;
  /** Shorter label for narrow layouts; accessible name uses accessibleLabel. */
  shortLabel: string;
  /** Spoken / SR-friendly name (CSV verb stays on the visible label). */
  accessibleLabel: string;
}> = [
  {
    action: "APPLY",
    label: "APPLY",
    shortLabel: "APPLY",
    accessibleLabel: "APPLY",
  },
  {
    action: "REAPPLY",
    label: "REAPPLY",
    shortLabel: "REAPPLY",
    accessibleLabel: "REAPPLY",
  },
  {
    action: "DELETE_KEEP_AMOUNT",
    label: "DELETE_KEEP_AMOUNT",
    shortLabel: "KEEP AMT",
    accessibleLabel: "Delete, keep amount",
  },
  {
    action: "DELETE",
    label: "DELETE",
    shortLabel: "DELETE",
    accessibleLabel: "DELETE",
  },
];

export interface MatchingBulkActionBarProps {
  selectedCount: number;
  totalCount: number;
  isDisabled?: boolean;
  onApply: (action: MatchingCsvAction, scope: MatchingBulkScope) => void;
  onClearSelection: () => void;
}

/**
 * Sticky bulk MatchAction toolbar (Product-PRDs #400). Shown after the user
 * selects rows — one click per verb applies to the selection (destructive
 * verbs still confirm in the composer). Passes scope `"selection"`; the
 * composer upgrades to `"all"` for confirm copy when every selectable row
 * is selected.
 */
export function MatchingBulkActionBar({
  selectedCount,
  totalCount,
  isDisabled = false,
  onApply,
  onClearSelection,
}: MatchingBulkActionBarProps) {
  return (
    <div
      className={styles.bar}
      role="group"
      aria-label="Bulk MatchAction"
    >
      <span className={styles.prompt}>Set selected to</span>
      <div className={styles.verbs} role="group" aria-label="MatchAction verbs">
        {VERB_BUTTONS.map(({ action, label, shortLabel, accessibleLabel }) => (
          <Button
            key={action}
            variant="secondary"
            onPress={() => onApply(action, "selection")}
            isDisabled={isDisabled || selectedCount === 0}
            aria-label={`Set selected rows to ${accessibleLabel}`}
          >
            <span className={styles.verbFull}>{label}</span>
            <span className={styles.verbShort}>{shortLabel}</span>
          </Button>
        ))}
      </div>

      <Button
        variant="tertiary"
        onPress={onClearSelection}
        isDisabled={isDisabled || selectedCount === 0}
      >
        Clear selection
      </Button>

      <p className={styles.status} role="status" aria-live="polite">
        {selectedCount} of {totalCount} row{totalCount === 1 ? "" : "s"}{" "}
        selected
      </p>
    </div>
  );
}
